import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.email.trim()) return toast.error("Please enter your email address.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return toast.error("Please enter a valid email address.");
    if (!form.message.trim()) return toast.error("Please enter a message.");

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      toast.success("Thanks for reaching out! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.status === 422
          ? "Please check your details and try again."
          : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-sm text-white placeholder-slate-500 focus:border-[#00DC82] focus:ring-1 focus:ring-[#00DC82] transition-colors outline-none";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden scroll-mt-24"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute bottom-0 left-1/4 h-[380px] w-[560px] rounded-full bg-[#00DC82]/[0.05] blur-[140px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left — copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-6"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#00DC82]">
              Contact
            </span>
            <h2
              data-testid="contact-title"
              className="text-3xl sm:text-4xl font-bold tracking-tighter text-white leading-[1.12]"
            >
              Get in touch.
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed max-w-md">
              Questions about MockTP, student progress or how it could support your driving
              school? Send us a message and we'll get back to you.
            </p>
            <a
              href="mailto:support@mocktp.app"
              data-testid="contact-email-link"
              className="inline-flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors w-fit"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.05] text-[#00DC82]">
                <Mail size={18} />
              </span>
              support@mocktp.app
            </a>
          </motion.div>

          {/* Right — form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="flex flex-col gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7 sm:p-8"
            noValidate
          >
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                data-testid="contact-name-input"
                aria-label="Your name"
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputCls}
              />
              <input
                type="email"
                data-testid="contact-email-input"
                aria-label="Your email"
                value={form.email}
                onChange={set("email")}
                placeholder="Your email"
                className={inputCls}
              />
            </div>
            <textarea
              data-testid="contact-message-input"
              aria-label="Your message"
              value={form.message}
              onChange={set("message")}
              placeholder="How can we help you?"
              rows={5}
              className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-[#00DC82] focus:ring-1 focus:ring-[#00DC82] transition-colors outline-none resize-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="h-12 rounded-full bg-[#00DC82] text-[#080C16] font-semibold hover:bg-[#00B86B] hover:shadow-[0_0_24px_rgba(0,220,130,0.4)] transition-[background-color,box-shadow] duration-200 flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  Send Message
                  <Send size={16} />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
